import { useMemo } from 'react'
import type { AgentOutputProps } from '../types'

/**
 * Renders PDF output — used by document-generation, latex-typesetting, and any
 * agent that returns { pdf_url } or { file_url } pointing at a .pdf.
 *
 * Uses <embed> which ships with every browser. No pdf.js bundle required.
 */
function extractPdfUrl(result: any, output: any): string | null {
  const pick = (o: any): string | null => {
    if (!o) return null
    if (typeof o === 'string') {
      const m = o.match(/https?:\/\/\S+\.pdf/) || o.match(/\/\S+\.pdf/)
      return m ? m[0] : null
    }
    return (
      o.pdf_url ||
      o.file_url?.toString()?.toLowerCase?.()?.endsWith?.('.pdf') ? o.file_url : null
    ) || null
  }
  const r = result || {}
  const ad = r.agent_data || {}
  return pick(r.pdf_url ? r : ad) || pick(r) || pick(output) || pick(r.output) || null
}

export default function PdfRenderer(props: AgentOutputProps) {
  const url = useMemo(
    () => extractPdfUrl(props.result, props.result?.output),
    [props.result],
  )

  if (!url) {
    return (
      <div className="text-sm text-muted-foreground italic">
        No PDF URL found in the agent result.
      </div>
    )
  }
  return (
    <div className="space-y-2">
      <embed
        src={url}
        type="application/pdf"
        className="w-full max-w-4xl rounded-lg border border-border bg-muted/20"
        style={{ height: 560 }}
      />
      <div className="text-xs text-muted-foreground flex gap-3">
        <a href={url} className="underline" target="_blank" rel="noreferrer">
          Open PDF in new tab
        </a>
        <a href={url} download className="underline">
          Download
        </a>
      </div>
    </div>
  )
}
